import { z } from 'zod';
import { AuditedFields, IsoDate, IsoDateTime, Uuid, Version } from './common.js';
/**
 * Suivi des dépenses (sprint 2.9, D-052).
 *
 * Une dépense est un décaissement de l'établissement, quel que soit le moyen :
 * espèces (sortie de caisse), mobile money, chèque, virement, retrait en banque.
 * Le module Caisse ne saisit plus de « dépense » : une dépense en espèces crée
 * elle-même le mouvement de sortie dans la session ouverte (A-058).
 * Une dépense ne se supprime pas : elle s'annule avec un motif (audit).
 */
export const ExpenseMethod = z.enum([
    'CASH',
    'MOBILE_MONEY',
    'CHEQUE',
    'BANK_TRANSFER',
    'BANK_WITHDRAWAL',
]);
export const ExpenseProvider = z.enum(['ORANGE_MONEY', 'MOOV_MONEY', 'WAVE', 'OTHER']);
/** Catégories initiales (A-060) ; libellés traduits côté web. */
export const ExpenseCategory = z.enum([
    'SUPPLIES',
    'FUEL',
    'MAINTENANCE',
    'TRANSPORT',
    'UTILITIES',
    'SALARY_ADVANCE',
    'TAXES_FEES',
    'BANK_DEPOSIT',
    'OTHER',
]);
export const ExpenseStatus = z.enum(['RECORDED', 'CANCELLED']);
export const Expense = z
    .object({
    id: Uuid,
    propertyId: Uuid,
    /** `EXP-AAAA-NNNNNN`, séquence par tenant et par exercice. */
    reference: z.string(),
    status: ExpenseStatus,
    /** Date de la dépense (jour civil de l'établissement). */
    expenseDate: IsoDate,
    amount: z.number().positive(),
    currencyCode: z.string().length(3),
    method: ExpenseMethod,
    provider: ExpenseProvider.nullable(),
    /** Numéro de chèque, référence de transaction mobile money ou bancaire. */
    transactionReference: z.string().nullable(),
    category: ExpenseCategory,
    /** Fournisseur ou bénéficiaire, texte libre (« Station Total Ouaga 2000 »). */
    beneficiary: z.string().nullable(),
    description: z.string(),
    /** Mouvement de caisse créé pour une dépense en espèces (A-058). */
    cashMovementId: Uuid.nullable(),
    cashSessionId: Uuid.nullable(),
    recordedBy: z.object({ id: Uuid, fullName: z.string() }),
    cancelledAt: IsoDateTime.nullable(),
    cancelledBy: z.object({ id: Uuid, fullName: z.string() }).nullable(),
    cancellationReason: z.string().nullable(),
    version: Version,
})
    .merge(AuditedFields);
export const RecordExpenseRequest = z
    .object({
    propertyId: Uuid.optional(),
    expenseDate: IsoDate,
    amount: z.number().positive(),
    method: ExpenseMethod,
    provider: ExpenseProvider.nullable().optional(),
    transactionReference: z.string().trim().max(80).nullable().optional(),
    category: ExpenseCategory,
    beneficiary: z.string().trim().max(120).nullable().optional(),
    description: z.string().trim().min(3).max(500),
})
    .superRefine((value, ctx) => {
    if (value.method === 'MOBILE_MONEY' && !value.provider) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['provider'],
            message: 'Opérateur mobile money requis',
        });
    }
    if (value.method === 'CHEQUE' && !value.transactionReference) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['transactionReference'],
            message: 'Numéro de chèque requis',
        });
    }
});
export const CancelExpenseRequest = z.object({
    reason: z.string().trim().min(5).max(500),
    version: Version,
});
export const ListExpensesQuery = z.object({
    from: IsoDate.optional(),
    to: IsoDate.optional(),
    propertyId: Uuid.optional(),
    method: ExpenseMethod.optional(),
    category: ExpenseCategory.optional(),
    status: ExpenseStatus.optional(),
    q: z.string().trim().max(80).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
});
/** Bandeau de la page Dépenses : totaux de la période, hors dépenses annulées. */
export const ExpensesSummary = z.object({
    currencyCode: z.string().length(3),
    period: z.object({ from: IsoDate, to: IsoDate }),
    total: z.number(),
    count: z.number().int(),
    byMethod: z.array(z.object({
        method: ExpenseMethod,
        provider: ExpenseProvider.nullable(),
        amount: z.number(),
        count: z.number().int(),
    })),
    byCategory: z.array(z.object({ category: ExpenseCategory, amount: z.number(), count: z.number().int() })),
});
//# sourceMappingURL=expenses.js.map